import Config from 'config';
import { delay } from 'redux-saga';
import { race, cancel, take, fork, all, call, put, select } from 'redux-saga/effects';
import R from 'ramda';
import request from '../../../lib/request';
import waitForSession from '../../../lib/wait-for-session';
import { getUserId } from '../../session/getters';
import {
  updateCostItemHF,
  updateTicketHF,
  cancelTransfer,
  transfer,
  resendTransfer,
  sendMessage,
  cancel as orderCancel
} from '../../../lib/api-helper';
import {
  allCurrentTicketData,
  allPastTicketData,
  selectedCostItem,
  selectedTicketId,
  selectedTransferId,
  ticketTransferInputValues,
  costItemForSelectedCostItemId,
  guestHostFields,
  buyerHostFields,
  getOpenedPass,
  selectedTicket,
  costItemsForSelectedTicket
} from '../getters';


import {
  ON_MY_TICKETS_CURRENT_MOUNT_SUCCESS,
  ON_MY_TICKETS_CURRENT_MOUNT_ERROR,
  ON_MY_TICKETS_PAST_MOUNT_SUCCESS,
  ON_MY_TICKETS_PAST_MOUNT_ERROR,
  ON_MY_TICKETS_MOUNT_ERROR,
  ON_MY_PASSES_MOUNT_SUCCESS,
  ON_MY_PASSES_MOUNT_ERROR,
  ON_COST_ITEMS_SUCCESS,
  ON_COST_ITEMS_ERROR,
  SEND_HOST_MESSAGE_SUCCESS,
  SEND_HOST_MESSAGE_ERROR,
  CANCEL_ORDER_SUCCESS,
  CANCEL_ORDER_ERROR,
  TRANSFER_TICKET_SUCCESS,
  TRANSFER_TICKET_ERROR,
  RESEND_TICKET_TRANSFER_SUCCESS,
  RESEND_TICKET_TRANSFER_ERROR,
  ON_COST_ITEM_UPDATE,
  CANCEL_TICKET_TRANSFER_SUCCESS,
  CANCEL_TICKET_TRANSFER_ERROR,
  UPDATE_COST_ITEM_HOST_FIELDS_SUCCESS,
  UPDATE_COST_ITEM_HOST_FIELDS_ERROR,
  UPDATE_TICKET_HOST_FIELDS_SUCCESS,
  UPDATE_TICKET_HOST_FIELDS_ERROR,
  STOP_COST_ITEM_SYNC,
  SHOW_FLASH,
  CLEAR_FLASH,
  SHOW_BANNER,
  ADD_FLASH_MESSAGE,
  ON_TOGGLE_PASS_SUCCESS,
  ON_TOGGLE_PASS_ERROR
} from '../actions';

// Constants
const MAX_CURRENT = 10;
const MAX_PAST = 5;


export function* requestAndPut(requestParameters, actionCreator, errorCreator = undefined) {
  try {
    const result = yield call(...requestParameters);
    yield put(actionCreator(result));
  }
  catch (e) {
    if (errorCreator) {
      yield put(errorCreator(e));
    }
  }
}

export const requestCostItemActionCreator = costItem => ({
  type: ON_COST_ITEM_UPDATE,
  payload: costItem ? costItem : false
});

export const requestCostItemsActionCreator = costItems => ({
  type: ON_COST_ITEMS_SUCCESS,
  payload: costItems ? costItems : false
});

export const requestCurrentTicketsActionCreator = tickets => ({
  type: ON_MY_TICKETS_CURRENT_MOUNT_SUCCESS,
  payload: tickets ? tickets : false
});

export const requestPastTicketsActionCreator = tickets => ({
  type: ON_MY_TICKETS_PAST_MOUNT_SUCCESS,
  payload: tickets ? tickets : false
});

export const requestCurrentTicketsErrorCreator = error => ({
  type: ON_MY_TICKETS_CURRENT_MOUNT_ERROR,
  payload: error ? error : false
});

export const requestPastTicketsErrorCreator = error => ({
  type: ON_MY_TICKETS_PAST_MOUNT_ERROR,
  payload: error ? error : false
});

export function requestTickets(type, userId, limit, offset = 0) {
  const options = {
    credentials: 'include'
  };

  return request(`${Config.api.base}/tickets?involving_user_id=${userId}&is_current_or_past=${type}&limit=${limit}&offset=${offset}&order=desc&sort=start_stamp`, options)
    .then(resp => resp.json())
    .catch(err => { throw err; });
}

export function requestCostItem(costItem) {
  const options = {
    credentials: 'include'
  };

  return request(`${Config.api.base}/cost_items/${costItem}`, options)
    .then(resp => resp.json());
}

function* waitForTixRequestsToFinish() {
  yield all([
    take(ON_MY_TICKETS_CURRENT_MOUNT_SUCCESS),
    take(ON_MY_TICKETS_PAST_MOUNT_SUCCESS)
  ]);

  return true;
}

export function* fetchMyTicketsData() {
  yield call(waitForSession);
  const userId = yield select(getUserId);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }

    const ticketCalls = yield all([
      fork(requestAndPut, [requestTickets, 'current', userId, MAX_CURRENT], requestCurrentTicketsActionCreator, requestCurrentTicketsErrorCreator),
      fork(requestAndPut, [requestTickets, 'past', userId, MAX_PAST], requestPastTicketsActionCreator, requestPastTicketsErrorCreator)
    ]);

    const { cancelSagas, success, currentError, pastError } = yield race({
      cancelSagas: take(STOP_COST_ITEM_SYNC),
      success: waitForTixRequestsToFinish(),
      currentError: take(ON_MY_TICKETS_CURRENT_MOUNT_ERROR),
      pastError: take(ON_MY_TICKETS_PAST_MOUNT_ERROR)
    });

    if (cancelSagas) {
      for (let i = 0; i < ticketCalls.length; i++) {
        yield cancel(ticketCalls[i]);
      }
    }
    else if (currentError) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'currentTicketsError' }});
    }
    else if (pastError) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'pastTicketsError' }});
    }
    else {
      return success;
    }
  }
  catch (e) {
    yield put({ type: ON_MY_TICKETS_MOUNT_ERROR, payload: e });
  }
}

export function* fetchCostItems(currentOrPast) {
  yield call(waitForSession);
  const userId = yield select(getUserId);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }

    const getTickets = currentOrPast === 'current' ? yield select(allCurrentTicketData) : yield select(allPastTicketData);

    if (!getTickets.tickets) {
      throw new Error(`No ${currentOrPast} CostItems`);
    }

    // Only query first 60 cost items of every ticket (view more link for rest)
    const costItemIds = getTickets.tickets.map(ticket => {
      return ticket.cost_item_ids.slice(0, 60);
    });

    const forks = R.flatten(costItemIds).map(costItem => {
      return fork(requestAndPut, [requestCostItem, costItem], requestCostItemsActionCreator);
    });

    const costItemCalls = yield all(forks);

    // wait for the user stop action
    yield take(STOP_COST_ITEM_SYNC);

    for (let i = 0; i < costItemCalls.length; i++) {
      yield cancel(costItemCalls[i]);
    }
  }
  catch (e) {
    yield put({ type: ON_COST_ITEMS_ERROR, payload: e });
  }
}

export function* fetchOrders({ payload }) {
  yield call(waitForSession);
  const userId = yield select(getUserId);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }

    if (payload.isCurrentOrPast === 'current') {
      yield fork(requestAndPut, [requestTickets, 'current', userId, MAX_CURRENT, payload.offset], requestCurrentTicketsActionCreator);
    }
    else {
      yield fork(requestAndPut, [requestTickets, 'past', userId, MAX_PAST, payload.offset], requestPastTicketsActionCreator);
    }
  }
  catch (e) {
    yield put({ type: ON_MY_TICKETS_MOUNT_ERROR, payload: e });
  }
}

function* showFlashMessage({ message, level }) {
  yield put({ type: CLEAR_FLASH });
  yield put({ type: SHOW_FLASH, message, level });
  yield call(delay, 5000);
}

export function* addFlashMessage({ payload }) {
  yield fork(showFlashMessage, payload);
}

// My Tickets
export function* cancelOrder()  {
  try {
    const ticketId =  yield select(selectedTicketId);
    const costItems = yield select(costItemsForSelectedTicket);

    const res = yield orderCancel(ticketId);

    if (res && res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'cancelOrderError' }});
    }
    else {
      yield put({ type: CANCEL_ORDER_SUCCESS, ticketId, costItems });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'cancelOrderSuccess' }});
    }
  }
  catch (e) {
    yield put({ type: CANCEL_ORDER_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'cancelOrderError' }});
  }
}

export function* sendHostMessage({ payload })  {
  try {
    const userId = yield select(getUserId);
    const data = yield sendMessage(payload.message, payload.hostId, userId, payload.link);

    yield put({ type: SEND_HOST_MESSAGE_SUCCESS, data });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'refundSuccess' }});
  }
  catch (e) {
    yield put({ type: SEND_HOST_MESSAGE_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'refundError' }});
  }
}

export function* transferTicket()  {
  try {
    const costItemId = yield select(selectedCostItem);
    const ticketRecipient = yield select(ticketTransferInputValues);
    const res = yield transfer(costItemId, ticketRecipient);

    if (res && res.errors) {
      yield put({ type: TRANSFER_TICKET_ERROR, e });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'transferTicketError' }});
    }
    else {
      yield put({ type: TRANSFER_TICKET_SUCCESS, costItemId });
      yield fork(requestAndPut, [requestCostItem, costItemId], requestCostItemActionCreator);
      yield put({ type: SHOW_BANNER, payload: { level: 'success', message: 'transferTicketBannerSuccess', values: { email: ticketRecipient.email }}});
    }
  }
  catch (e) {
    yield put({ type: TRANSFER_TICKET_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'transferTicketError' }});
  }
}

export function* resendTicketTransfer() {
  try {
    const costItemId = yield select(selectedCostItem);
    const transferId = yield select(selectedTransferId);

    const res = yield resendTransfer(transferId);
    if (res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'resendTransferError' }});
    }
    else {
      yield put({ type: RESEND_TICKET_TRANSFER_SUCCESS, transferId });
      yield fork(requestAndPut, [requestCostItem, costItemId], requestCostItemActionCreator);
      const costItem = yield select(costItemForSelectedCostItemId);
      const email = costItem.transfers[0].email;

      yield put({ type: SHOW_BANNER, payload: { level: 'success', message: 'resendTransferBannerSuccess', values: { email }}});
    }
  }
  catch (e) {
    yield put({ type: RESEND_TICKET_TRANSFER_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'resendTransferError' }});
  }
}

export function* cancelTicketTransfer()  {
  try {
    const costItemId = yield select(selectedCostItem);
    const costItem = yield select(costItemForSelectedCostItemId);
    const transferId = yield select(selectedTransferId);

    const res = yield cancelTransfer(transferId);
    if (res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'cancelTransferError' }});
    }
    else {
      yield put({ type: CANCEL_TICKET_TRANSFER_SUCCESS, transferId });

      yield put({ type: SHOW_BANNER, payload: { level: 'success', message: 'cancelTransferBannerSuccess', values: { email: costItem.transfers[0].email }}});

      yield fork(requestAndPut, [requestCostItem, costItemId], requestCostItemActionCreator);
    }
  }
  catch (e) {
    yield put({ type: CANCEL_TICKET_TRANSFER_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'cancelTransferError' }});
  }
}

export function* updateCostItemHostFields()  {
  try {
    const costItemId = yield select(selectedCostItem);
    const hostFields = yield select(costItemForSelectedCostItemId);
    const updatedValues = yield select(guestHostFields);

    const res = yield updateCostItemHF(costItemId, hostFields, updatedValues);

    if (res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'updateGuestInfoError' }});
    }
    else {
      yield put({ type: UPDATE_COST_ITEM_HOST_FIELDS_SUCCESS, costItemId });
      yield fork(requestAndPut, [requestCostItem, costItemId], requestCostItemActionCreator);
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'updateGuestInfoSuccess' }});
    }
  }
  catch (e) {
    yield put({ type: UPDATE_COST_ITEM_HOST_FIELDS_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'updateGuestInfoError' }});
  }
}

export function* updateTicketHostFields()  {
  try {
    const ticketId = yield select(selectedTicketId);
    const ticket = yield select(selectedTicket);
    const updatedValues = yield select(buyerHostFields);

    const res = yield updateTicketHF(ticketId, ticket, updatedValues);

    if (res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'updateBuyerInfoError' }});
    }
    else {
      yield put({ type: UPDATE_TICKET_HOST_FIELDS_SUCCESS, ticketId });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'updateBuyerInfoSuccess' }});
    }
  }
  catch (e) {
    yield put({ type: UPDATE_TICKET_HOST_FIELDS_ERROR, e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'updateBuyerInfoError' }});
  }
}


// Passes

export const requestPassesActionCreator = passes => ({
  type: ON_MY_PASSES_MOUNT_SUCCESS,
  payload: passes ? passes : false
});

export function requestPasses(userId, limit = 10, offset = 0) {
  const options = {
    credentials: 'include'
  };

  return request(`${Config.api.base}/passes?involving_user_id=${userId}&limit=${limit}&offset=${offset}&order=desc&sort=created_at`, options)
    .then(resp => resp.json());
}


export function* fetchMyPassesData() {
  yield call(waitForSession);
  const userId = yield select(getUserId);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }

    yield fork(requestAndPut, [requestPasses, userId, MAX_CURRENT], requestPassesActionCreator);
  }
  catch (e) {
    yield put({ type: ON_MY_PASSES_MOUNT_ERROR, payload: e });
  }
}

export function requestListingsForThisPass(ticketTypeGroupId) {
  const options = { credentials: 'include' };
  return request(`${Config.api.base}/listings?limit=100&offset=0&order=desc&show=true&sort=created_at&ticket_type_group_id=${ticketTypeGroupId}`, options)
    .then(resp => resp.json());
}

export const requestListingsForThisPassActionCreator = listingsForThisPass => ({
  type: ON_TOGGLE_PASS_SUCCESS,
  payload: listingsForThisPass ? listingsForThisPass : false
});

export function* togglePassOpen() {
  yield call(waitForSession);
  const pass = yield select(getOpenedPass);
  const ticketTypeGroupId = pass ? pass.ticket_type_group_id : false;

  try {
    // failsafe
    if (!ticketTypeGroupId) {
      throw new Error('No ticket_type_group_id found');
    }

    yield fork(requestAndPut, [requestListingsForThisPass, ticketTypeGroupId], requestListingsForThisPassActionCreator);
  }
  catch (e) {
    yield put({ type: ON_TOGGLE_PASS_ERROR, payload: e });
  }
}

export function redirectToSignIn(path) {
  window.location.href = `${Config.api.host}/sign-in?redirectTo=${path}`;
}



// WEBPACK FOOTER //
// ./src/modules/my-tickets/actions/sagas.js